import { Injectable } from '@angular/core';
import {
  getComparisonColumnCaption,
  type ComparisonRanges,
  type ComparisonTableDefinition,
  type ReportingComparisonRowDto,
} from 'ag-common-lib/lib/contracts/reporting-api.contracts';
import { BlockDatasetOptions } from './production-comparison-dashboard.models';
import {
  type ProductionComparisonExportColumn,
  type ProductionComparisonExportSheet,
} from './production-comparison-export.service';

/**
 * What the page calls things, and which of its columns it is showing.
 *
 * The builder knows the shape of a sheet. Only the page knows the period on screen and whether
 * the viewer has hidden a column, so it hands those over rather than having the builder reach
 * back into it.
 */
export interface ComparisonExportContext {
  pageTitle: string;
  isColumnShown(field: string): boolean;
  /** The ranges on screen, which name the figure columns: "2026 Premium", "2025 Policies". */
  ranges: ComparisonRanges;
}

/**
 * Turns what the page is showing into the columns, names and sheets the export is written from.
 *
 * Kept apart from the service that writes the workbook: that one knows about ExcelJS and
 * nothing about this report, and this one is the other way round.
 */
@Injectable()
export class ProductionComparisonExportBuilderService {
  /**
   * One table's columns, as the catalog declares them for the grid the table is.
   *
   * Hidden columns are dropped, so a sheet carries what the page is showing. The name column is
   * never among them: it is not in the picker, and rows without a name are not worth exporting.
   * A sheet may name it for itself — the overall row is an entity, not an agency.
   */
  buildTableColumns(
    context: ComparisonExportContext,
    table: ComparisonTableDefinition,
    entityHeader: string = table.entityCaption,
  ): ProductionComparisonExportColumn[] {
    return table.columns
      .filter(column => column.field === 'entityName' || context.isColumnShown(column.field))
      .map(column => ({
        key: column.field,
        header:
          column.field === 'entityName'
            ? entityHeader
            : getComparisonColumnCaption(column.field, table, context.ranges),
        kind: column.kind,
      }));
  }

  /**
   * Every MGA behind a dataset, as its own sheet.
   *
   * The movers are the few at each end and the full list on the page is a page of it; neither is
   * the set. This carries the whole thing, narrowed by whatever the table is filtered to, with
   * no cards or charts beside it — a sheet somebody can sort and pivot for themselves.
   */
  buildAllMgasSheet(
    context: ComparisonExportContext,
    table: ComparisonTableDefinition,
    rows: ReportingComparisonRowDto[],
  ): ProductionComparisonExportSheet {
    const label = table.dataset === 'paid' ? 'Paid' : 'Submitted';

    return {
      sheetName: `All MGAs (${label})`,
      title: context.pageTitle,
      subtitle: `Every MGA, ${label.toLowerCase()} production`,
      sections: [
        {
          title: `All MGAs — ${label} Production`,
          description: `Every MGA matching the filters the page is showing — ${rows.length} in total.`,
          cards: [],
          overallTable: {
            title: 'All MGAs',
            columns: this.buildTableColumns(context, table),
            rows,
          },
        },
      ],
    };
  }

  buildWorkbookFileName(scopeLabel: string, setupSummary: string): string {
    const scope = scopeLabel.replace(/\s+/g, '-').toLowerCase();
    const summary = setupSummary
      .replace(/[^a-z0-9]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase();

    return `comparison-dashboard-${scope}-${summary}`;
  }

  /** What a single full-list download is called, which reads as a date rather than a slug. */
  buildEntitiesFileName(entityLabel: 'Agencies' | 'Agents', block: BlockDatasetOptions, stopDate: string): string {
    const blockLabel = block === BlockDatasetOptions.paid ? 'Paid' : 'Submitted';

    return `YTD ${this.formatStopDate(stopDate)} ${entityLabel} ${blockLabel} Production`;
  }

  private formatStopDate(isoDate: string): string {
    const [year, month, day] = isoDate.split('-');

    return `${month}.${day}.${year}`;
  }
}
